import type { ReactNode } from "react";
import { cx } from "../cx";

/**
 * White bordered panel. `label` is the mono uppercase caption (BOOK, PARSE REPORT),
 * `title` the serif page or card title. `tone="warn"` is the amber notice card.
 */
export function Card({
  label,
  title,
  titleSize = "md",
  titleAs: Heading = "h2",
  description,
  actions,
  tone = "surface",
  padding = "regular",
  children,
  className = "",
}: {
  label?: ReactNode;
  title?: ReactNode;
  titleSize?: "sm" | "md" | "lg";
  titleAs?: "h1" | "h2" | "h3";
  /** Grey sentence next to or under the title. */
  description?: ReactNode;
  /** Right-aligned slot in the header row. */
  actions?: ReactNode;
  tone?: "surface" | "sunken" | "warn";
  /** `none` for cards whose children are tables that bleed to the edge. */
  padding?: "regular" | "none";
  children?: ReactNode;
  className?: string;
}) {
  const TONE = {
    surface: "border-line bg-surface",
    sunken: "border-line-soft bg-sunken",
    warn: "border-warn-line bg-warn-soft",
  } as const;
  const TITLE = {
    sm: "text-title-sm",
    md: "text-title",
    lg: "text-title-lg",
  } as const;
  const hasHeader = label || title || description || actions;
  return (
    <section
      data-component="Card"
      data-tone={tone}
      className={cx("flex min-w-0 flex-col rounded-md border", TONE[tone], className)}
    >
      {hasHeader ? (
        <header
          className={cx(
            "flex flex-col gap-1.5",
            padding === "none" ? "px-6 pt-5 pb-3" : "px-5 pt-4 pb-3",
          )}
        >
          {label ? <p className="label-mono">{label}</p> : null}
          {title || actions ? (
            <div className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:gap-4">
              {title ? (
                <Heading
                  className={cx("break-words font-serif font-semibold text-ink", TITLE[titleSize])}
                >
                  {title}
                </Heading>
              ) : null}
              {description ? <p className="text-ui text-ink-2">{description}</p> : null}
              {actions ? <div className="ml-auto flex items-center gap-2">{actions}</div> : null}
            </div>
          ) : description ? (
            <p className="text-ui text-ink-2">{description}</p>
          ) : null}
        </header>
      ) : null}
      {children !== undefined ? (
        <div
          className={cx(
            "flex min-w-0 flex-1 flex-col",
            padding === "regular" && (hasHeader ? "px-5 pb-4" : "px-5 py-4"),
          )}
        >
          {children}
        </div>
      ) : null}
    </section>
  );
}
